import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import PageHeader from '../../components/common/PageHeader';
import { Link } from 'react-router-dom';
import { FaBalanceScale, FaChartLine, FaShieldAlt, FaTasks } from 'react-icons/fa';

const PortfolioManagementServicePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const breadcrumbs = [
    { label: 'Services', path: '/services' },
    { label: 'Portfolio Management Service' }
  ];

  const portfolioServices = [
    {
      icon: <FaBalanceScale className="w-8 h-8 text-primary" />,
      title: "Portfolio Optimization",
      description: "Strategic asset allocation to maximize returns while maintaining your desired risk level through advanced optimization techniques."
    },
    {
      icon: <FaChartLine className="w-8 h-8 text-primary" />,
      title: "Performance Monitoring",
      description: "Continuous tracking of portfolio performance against custom benchmarks with comprehensive reports and analytics."
    },
    {
      icon: <FaShieldAlt className="w-8 h-8 text-primary" />,
      title: "Risk Management",
      description: "Multi-layered risk management approach integrating diversification, hedging strategies, and ongoing market analysis."
    },
    {
      icon: <FaTasks className="w-8 h-8 text-primary" />,
      title: "Dynamic Rebalancing",
      description: "Systematic portfolio rebalancing to maintain optimal asset allocation and capture value from market movements."
    }
  ];

  const managementApproach = [
    {
      title: "Assessment & Strategy",
      description: "We begin with a comprehensive assessment of your financial situation, goals, and risk tolerance to develop a tailored portfolio strategy."
    },
    {
      title: "Investment Selection",
      description: "Our investment team conducts rigorous analysis to select the most suitable investments across asset classes for your portfolio."
    },
    {
      title: "Implementation",
      description: "We execute your portfolio strategy with precision, considering tax efficiency and transaction costs."
    },
    {
      title: "Continuous Oversight",
      description: "Your portfolio is actively monitored and adjusted as market conditions change, with regular reviews and transparent reporting."
    }
  ];

  return (
    <>
      <PageHeader 
        title="Portfolio Management Service" 
        breadcrumbs={breadcrumbs}
      />
      
      <section className="py-8 md:py-24 bg-background">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6"
            >
              <h2 className="text-3xl md:text-4xl font-heading font-bold text-primary">
                Professional Portfolio Management
              </h2>
              <p className="text-lg text-gray-700">
                Our Portfolio Management Service provides comprehensive, professional oversight of your investment portfolio to ensure it remains optimally positioned to meet your financial objectives.
              </p>
              <p className="text-lg text-gray-700">
                We combine sophisticated investment strategies, advanced technology, and personalized service to maximize your portfolio's potential while carefully managing risk.
              </p>
              <div className="pt-4">
                <Link 
                  to="/contact-us" 
                  className="px-8 py-3 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-lg transition-colors inline-block"
                >
                  Request Portfolio Review
                </Link>
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
            >
              <img 
                src="/assets/workmates-with-statistic-documents.jpg" 
                alt="Portfolio Management Team" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary mb-4">
              Our Portfolio Management Services
            </h3>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              We offer a comprehensive range of portfolio management services designed to optimize your investments and provide peace of mind.
            </p>
          </motion.div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-20">
            {portfolioServices.map((service, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all duration-300"
              >
                <div className="mb-4">{service.icon}</div>
                <h4 className="text-xl font-heading font-semibold mb-3">{service.title}</h4>
                <p className="text-gray-600">{service.description}</p>
              </motion.div>
            ))}
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-16">
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="order-2 lg:order-1"
            >
              <img 
                src="/assets/financial-charts-computer.jpg/financial-charts-computer.jpg" 
                alt="Portfolio Analysis" 
                className="rounded-xl shadow-lg w-full h-auto object-cover"
              />
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
              viewport={{ once: true }}
              className="space-y-6 order-1 lg:order-2"
            >
              <h3 className="text-2xl md:text-3xl font-heading font-bold text-primary">
                Our Management Approach
              </h3>
              <p className="text-lg text-gray-700">
                We follow a structured yet flexible approach to managing your portfolio:
              </p>
              
              <div className="space-y-4">
                {managementApproach.map((step, index) => (
                  <div key={index} className="bg-white p-6 rounded-lg shadow-sm border-l-4 border-gray-700">
                    <h4 className="text-xl font-heading font-semibold mb-2 text-primary">{step.title}</h4>
                    <p className="text-gray-600">{step.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
          
          <div className="bg-primary text-white rounded-xl p-10 shadow-lg">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-primary mb-2">97%</div>
                <div className="text-gray-300">Client Retention Rate</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-primary mb-2">20+</div>
                <div className="text-gray-300">Years of Experience</div>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
                className="text-center"
              >
                <div className="text-4xl font-bold text-primary mb-2">1,200+</div>
                <div className="text-gray-300">Portfolios Managed</div>
              </motion.div>
            </div>
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="text-center mt-10"
            >
              <Link 
                to="/contact-us" 
                className="px-8 py-3 bg-white text-primary hover:bg-gray-100 font-semibold rounded-lg transition-colors inline-block"
              >
                Begin Your Portfolio Optimization
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  );
};

export default PortfolioManagementServicePage;
